// src/pages/ManagedAuthorConsole.jsx
// Phase P3.1: Managed Author Console - mirrors Independent Author Console
// Publishers operate on behalf of Managed Authors
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PublisherNav from '../components/PublisherNav';
// P0 DIRECTIVE: TTSProgressModal is DEPRECATED and FORBIDDEN
// TTS is background-only, progress shown via polling surfaces
import AboutAuthorModal from '../components/AboutAuthorModal';
import OutlineEditorModal from '../components/outline/OutlineEditorModal';
import { 
  Upload, BarChart3, Edit, Eye, TrendingUp, Clock, Award, Plus, 
  ExternalLink, Archive, Trash2, ArrowLeft, User, Loader2, 
  BookOpen, CheckCircle, AlertCircle, FileText, RefreshCw, DollarSign, Users, Volume2, Coins, Brain, Music
} from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNotification } from '@/contexts/NotificationContext';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const ManagedAuthorConsole = () => {
  const { authorId } = useParams();
  const navigate = useNavigate();
  
  // Author data
  const { notifyError } = useNotification();
  const [author, setAuthor] = useState(null);
  const [imprintLogoUrl, setImprintLogoUrl] = useState(null);
  
  // Works (titles with manuscripts)
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selected work for details panel
  const [selectedTitle, setSelectedTitle] = useState(null);
  
  // Metrics
  const [metrics, setMetrics] = useState({
    totalTitles: 0,
    activeTitles: 0,
    totalReaderCredits: 0,
    totalUniqueReaders: 0
  });
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [titleToDelete, setTitleToDelete] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [showAboutAuthorModal, setShowAboutAuthorModal] = useState(false);
  
  // P0 DIRECTIVE: TTSProgressModal state removed - no longer used
  
  // Outline Editor Modal
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  const [outlineManuscript, setOutlineManuscript] = useState(null);
  
  // Polling
  const pollingRef = useRef(null);

  useEffect(() => {
    loadData();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [authorId]);
  
  // Poll for processing titles
  useEffect(() => {
    const processingTitles = titles.filter(t => t.status === 'processing');
    
    if (processingTitles.length === 0) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }
    
    pollingRef.current = setInterval(async () => {
      await loadData();
    }, 3000);
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [titles]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Load author data
      const authorRes = await fetch(`${API}/publisher/authors/${authorId}`, config);
      if (!authorRes.ok) {
        notifyError('Author not found');
        navigate('/publisher/authors');
        return;
      }
      const authorData = await authorRes.json();
      setAuthor(authorData);
      setImprintLogoUrl(authorData.headshot_fallback_url);

      // Load titles for this author
      const titlesRes = await fetch(`${API}/publisher/titles?publisher_author_id=${authorId}`, config);
      if (titlesRes.ok) {
        const titlesData = await titlesRes.json();
        setTitles(titlesData.titles || []);
        
        // Calculate metrics from titles data
        const activeTitles = (titlesData.titles || []).filter(t => t.status === 'active');
        
        // Sum total Reader Credits from all titles (ledger-based)
        const totalReaderCredits = (titlesData.titles || []).reduce((sum, t) => sum + (t.total_reader_credits || 0), 0);
        
        // Use the pre-calculated total_unique_readers from backend (counts unique readers across ALL titles)
        // This correctly counts a reader once even if they've read multiple books
        const totalUniqueReaders = titlesData.total_unique_readers || 0;
        
        setMetrics({
          totalTitles: titlesData.total || 0,
          activeTitles: activeTitles.length,
          totalReaderCredits: totalReaderCredits,
          totalUniqueReaders: totalUniqueReaders
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      notifyError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (title) => {
    const status = title.is_active ? 'active' : (title.status || 'draft');
    
    switch(status) {
      case 'active':
        return { 
          label: 'Active', 
          color: 'bg-emerald-900/40 text-emerald-300 border border-emerald-700/50',
          icon: CheckCircle
        };
      case 'processing':
        return { 
          label: 'Processing', 
          color: 'bg-amber-900/40 text-amber-300 border border-amber-700/50',
          icon: Loader2,
          isProcessing: true
        };
      case 'failed':
        return { 
          label: 'Failed', 
          color: 'bg-red-900/40 text-red-300 border border-red-700/50',
          icon: AlertCircle,
          isFailed: true
        };
      case 'inactive':
        return { 
          label: 'Inactive', 
          color: 'bg-slate-800/40 text-slate-400 border border-slate-600/50',
          icon: Archive
        };
      case 'archived':
        return { 
          label: 'Archived', 
          color: 'bg-slate-800/40 text-slate-400 border border-slate-700/50',
          icon: Archive
        };
      default:
        return { 
          label: 'Draft', 
          color: 'bg-purple-900/40 text-purple-300 border border-purple-700/50',
          icon: FileText
        };
    }
  };

  const getCoverUrl = (title) => {
    if (title.cover_image_url) {
      return `${BACKEND_URL}${title.cover_image_url}?t=${Date.now()}`;
    }
    return null;
  };

  const getHeadshotUrl = () => {
    if (author?.headshot_image_url) {
      return `${BACKEND_URL}${author.headshot_image_url}?t=${Date.now()}`;
    }
    if (imprintLogoUrl) {
      return `${BACKEND_URL}${imprintLogoUrl}?t=${Date.now()}`;
    }
    return null;
  };

  const handlePreview = async (title) => {
    if (!title.manuscript_id) {
      notifyError('No manuscript available for preview');
      return;
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/manuscripts/${title.manuscript_id}/open`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/player/${data.session_id}`, { state: { returnTo: `/publisher/authors/${authorId}/console` } });
      } else {
        notifyError('Failed to open preview');
      }
    } catch (error) {
      console.error('Error opening preview:', error);
      notifyError('Failed to open preview');
    }
  };

  // Handle checking TTS status for a title - P0: Navigate to edit page instead of modal
  const handleCheckTTSStatus = (title) => {
    if (!title.id) {
      notifyError('No title ID available');
      return;
    }
    // P0 DIRECTIVE: No modal - navigate to the title edit page which shows TTS status
    navigate(`/publisher/titles/${title.id}/edit`);
  };

  const handleArchiveTitle = (title) => {
    setArchiveTarget(title);
    setShowArchiveModal(true);
  };

  const handleArchiveToggle = async () => {
    if (!archiveTarget) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const newStatus = archiveTarget.status === 'archived' ? 'draft' : 'archived';
      
      const response = await fetch(`${API}/publisher/titles/${archiveTarget.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        console.log('[SUCCESS] ' + newStatus === 'archived' ? 'Title archived' : 'Title unarchived');
        setShowArchiveModal(false);
        setArchiveTarget(null);
        await loadData();
      } else {
        notifyError('Failed to update title');
      }
    } catch (error) {
      console.error('Error updating title:', error);
      notifyError('Failed to update title');
    }
  };

  const handleDeleteTitle = async () => {
    if (!titleToDelete) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API}/publisher/titles/${titleToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        console.log('[SUCCESS] ' + 'Title deleted successfully');
        setShowDeleteModal(false);
        setTitleToDelete(null);
        setSelectedTitle(null);
        await loadData();
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to delete title');
      }
    } catch (error) {
      console.error('Error deleting title:', error);
      notifyError('Failed to delete title');
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50 overflow-x-hidden">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
        </div>
      </div>
    );
  }

  const headshotUrl = getHeadshotUrl();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-x-hidden">
      <PublisherNav />

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Back Navigation */}
        <Link
          to="/publisher/authors"
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Authors
        </Link>

        {/* Console Header with Authority Context */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Managed Author Console</h1>
            <span className="px-3 py-1 rounded-full bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-xs font-medium">
              Operated by Publisher
            </span>
          </div>
          <p className="text-slate-400 text-sm">
            Uploads and processing are performed under the Publisher's authority.
          </p>
        </div>

        {/* Author Identity Card + At a Glance - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Column 1: Author Identity Card */}
          <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <div className="flex items-start gap-6">
              {headshotUrl ? (
                <img
                  src={headshotUrl}
                  alt={author?.pen_name}
                  className="w-20 h-20 rounded-xl object-cover border border-slate-600"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-slate-800 border border-slate-600 flex items-center justify-center">
                  <User className="w-10 h-10 text-slate-500" />
                </div>
              )}
              
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-100">{author?.pen_name}</h2>
                {author?.real_name && (
                  <p className="text-sm text-slate-400">Internal: {author.real_name}</p>
                )}
                <div className="flex flex-col gap-3 mt-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowAboutAuthorModal(true)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 text-sm text-indigo-300 transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View Author Profile
                    </button>
                    <Link
                      to={`/publisher/authors/${authorId}/edit`}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm text-slate-300 transition-colors"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      Edit Profile
                    </Link>
                  </div>
                  {/* Upload New Manuscript - Uses Simple Ingestion */}
                  <button
                    onClick={() => navigate(`/publisher/authors/${authorId}/upload`)}
                    data-testid="upload-manuscript-btn"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white transition-all w-fit shadow-lg hover:shadow-indigo-500/25"
                  >
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Upload className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <span className="font-semibold block">Upload New Manuscript</span>
                      <span className="text-xs text-indigo-200">Add a new title for {author?.pen_name}</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: At a Glance Metrics */}
          <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h3 className="text-lg font-bold text-white mb-4">At a Glance</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-4 text-white shadow-lg border border-purple-500/20">
                <div className="flex items-center justify-between mb-1">
                  <BookOpen className="w-5 h-5 opacity-80" />
                  <span className="text-2xl font-bold">{formatNumber(metrics.totalTitles)}</span>
                </div>
                <p className="text-purple-100 font-medium text-xs">Total Titles</p>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-4 text-white shadow-lg border border-emerald-500/20">
                <div className="flex items-center justify-between mb-1">
                  <CheckCircle className="w-5 h-5 opacity-80" />
                  <span className="text-2xl font-bold">{formatNumber(metrics.activeTitles)}</span>
                </div>
                <p className="text-emerald-100 font-medium text-xs">Active Titles</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-4 text-white shadow-lg border border-blue-500/20">
                <div className="flex items-center justify-between mb-1">
                  <Award className="w-5 h-5 opacity-80" />
                  <span className="text-2xl font-bold">{metrics.totalReaderCredits.toFixed(2)}</span>
                </div>
                <p className="text-blue-100 font-medium text-xs">Reader Credits</p>
              </div>
              
              <div className="bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl p-4 text-white shadow-lg border border-amber-500/20">
                <div className="flex items-center justify-between mb-1">
                  <Users className="w-5 h-5 opacity-80" />
                  <span className="text-2xl font-bold">{formatNumber(metrics.totalUniqueReaders)}</span>
                </div>
                <p className="text-amber-100 font-medium text-xs">Total Readers</p>
              </div>
            </div>
          </div>
        </div>

        {/* My NewAudia Titles List */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">My NewAudia Titles</h2>
            <button
              onClick={loadData}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
          
          {titles.length === 0 ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl shadow-md p-8 text-center backdrop-blur-sm">
              <div className="mx-auto w-14 h-14 rounded-full bg-indigo-600/20 flex items-center justify-center mb-4">
                <BookOpen className="w-7 h-7 text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-200 mb-2">No Works Yet</h3>
              <p className="text-slate-400 max-w-sm mx-auto mb-6">
                Upload a manuscript to create the first work for {author?.pen_name}.
              </p>
              <Button 
                onClick={() => navigate(`/publisher/authors/${authorId}/titles/new`)}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload First Manuscript
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {titles.map(title => {
                const status = getStatusBadge(title);
                const StatusIcon = status.icon;
                const coverUrl = getCoverUrl(title);
                
                return (
                  <Link 
                    key={title.id}
                    to={`/publisher/titles/${title.id}/edit`}
                    className="block bg-slate-800/50 border border-slate-700 rounded-xl shadow-md p-5 hover:shadow-lg hover:border-indigo-500/50 transition-all cursor-pointer backdrop-blur-sm"
                  >
                    <div className="flex items-start gap-4">
                      {/* Cover Thumbnail */}
                      <div className="w-16 h-20 rounded-lg bg-slate-700 flex-shrink-0 overflow-hidden">
                        {coverUrl ? (
                          <img src={coverUrl} alt={title.title_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-slate-500" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white truncate">{title.title_name}</h3>
                        {title.subtitle && (
                          <p className="text-sm text-slate-400 truncate">{title.subtitle}</p>
                        )}
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                            <StatusIcon className={`w-3 h-3 ${status.isProcessing ? 'animate-spin' : ''}`} />
                            {status.label}
                          </span>
                          {/* Audio Processing Status Badge */}
                          {title.audio_status === 'processing' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleCheckTTSStatus(title); }}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-900/40 text-purple-300 border border-purple-700/50 hover:bg-purple-800/50 cursor-pointer transition-colors"
                            >
                              <Volume2 className="w-3 h-3 animate-pulse" />
                              Audio: {title.tts_segments_completed || 0}/{title.tts_segments_total || '?'}
                            </button>
                          )}
                          {title.audio_status === 'completed' && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-900/40 text-green-300 border border-green-700/50">
                              <Volume2 className="w-3 h-3" />
                              Audio Ready
                            </span>
                          )}
                          {/* {title.api_cost_total > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700/50">
                              <DollarSign className="w-3 h-3" />
                              ${title.api_cost_total.toFixed(4)}
                            </span>
                          )} */}
                          {/* Reader Credits Badge */}
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-cyan-900/40 text-cyan-300 border border-cyan-700/50">
                            <Coins className="w-3 h-3" />
                            {(title.total_reader_credits || 0).toFixed(2)} Credits
                          </span>
                          {/* EI Status Badge */}
                          {title.ei_status && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                              title.ei_status === 'completed' ? 'bg-purple-900/40 text-purple-300 border-purple-700/50' :
                              title.ei_status === 'running' ? 'bg-blue-900/40 text-blue-300 border-blue-700/50' :
                              title.ei_status === 'failed' ? 'bg-red-900/40 text-red-300 border-red-700/50' :
                              'bg-slate-800/40 text-slate-400 border-slate-700/50'
                            }`}>
                              <Brain className={`w-3 h-3 ${title.ei_status === 'running' ? 'animate-pulse' : ''}`} />
                              {title.ei_status === 'completed' ? 'EI Ready' :
                               title.ei_status === 'running' ? `EI ${title.ei_completed || 0}/${title.ei_total || 0}` :
                               title.ei_status === 'failed' ? 'EI Failed' :
                               title.ei_status === 'queued' ? 'EI Queued' : title.ei_status}
                            </span>
                          )}
                          {/* Preview Available Badge - for books with samples */}
                          {title.hasSample && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-teal-900/40 text-teal-300 border border-teal-700/50">
                              <Music className="w-3 h-3" />
                              Preview Available
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Check TTS Status button - shown when audio is processing */}
                        {title.audio_status === 'processing' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleCheckTTSStatus(title); }}
                            className="p-2 rounded-lg bg-purple-700 hover:bg-purple-600 text-white transition-colors animate-pulse"
                            title="Check Audio Status"
                          >
                            <Volume2 className="w-4 h-4" />
                          </button>
                        )}
                        <span
                          className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors flex items-center gap-2"
                        >
                          <BookOpen className="w-4 h-4" />
                          Book Details
                        </span>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            e.preventDefault();
                            handleArchiveTitle(title);
                          }}
                          className="p-2 rounded-lg bg-slate-700 hover:bg-amber-900/50 hover:text-amber-400 text-slate-400 transition-colors"
                          title="Archive"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            e.preventDefault();
                            setTitleToDelete(title);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 rounded-lg bg-slate-700 hover:bg-red-900/50 hover:text-red-400 text-slate-400 transition-colors"
                          title="Delete Title and All Artifacts"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Archive Modal */}
      <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-slate-400" />
              {archiveTarget?.status === 'archived' ? 'Unarchive Title' : 'Archive Title'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {archiveTarget?.status === 'archived' 
                ? `Are you sure you want to unarchive "${archiveTarget?.title_name}"?`
                : `Are you sure you want to archive "${archiveTarget?.title_name}"? It will be hidden from readers.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <button
              onClick={() => setShowArchiveModal(false)}
              className="px-5 py-2 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleArchiveToggle}
              className="px-5 py-2 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition-colors"
            >
              {archiveTarget?.status === 'archived' ? 'Unarchive' : 'Archive'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Title Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete Title & All Artifacts
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-slate-400 space-y-2">
                <div>Are you sure you want to permanently delete "<span className="text-white font-medium">{titleToDelete?.title_name}</span>"?</div>
                <div className="text-xs text-red-400/80">This will remove:</div>
                <ul className="text-xs text-slate-500 list-disc list-inside space-y-1">
                  <li>Title cover image</li>
                  <li>Manuscript source file (EPUB/DOCX)</li>
                  <li>All generated audio files (TTS)</li>
                  <li>Vector store data</li>
                  <li>Reader library entries</li>
                </ul>
                <div className="text-xs text-red-400 font-semibold pt-2">This action cannot be undone.</div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setTitleToDelete(null);
              }}
              className="px-5 py-2 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteTitle}
              className="px-5 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold transition-colors"
            >
              Delete Permanently
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 DIRECTIVE: TTSProgressModal is FORBIDDEN and has been REMOVED */}
      {/* TTS progress is shown via polling on the titles list page */}

      {/* About Author Modal (Reader-facing view) */}
      <AboutAuthorModal
        isOpen={showAboutAuthorModal}
        onClose={() => setShowAboutAuthorModal(false)}
        authorId={authorId}
        authorType="managed"
      />

      {/* Outline Editor Modal */}
      <OutlineEditorModal
        manuscript={outlineManuscript}
        isOpen={showOutlineModal}
        onClose={() => {
          setShowOutlineModal(false);
          setOutlineManuscript(null);
        }}
        onSuccess={() => {
          loadData();
        }}
      />
      <UniversalFooter />
    </div>
  );
};

export default ManagedAuthorConsole;
